import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import QRGenerationPanel from './components/QRGenerationPanel';
import ActiveQRCodesList from './components/ActiveQRCodesList';
import QRCodeHistoryPanel from './components/QRCodeHistoryPanel';

const QRCodeManagementCenter = () => {
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, profileLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [myQRCodes, setMyQRCodes] = useState([]);
  const [allQRCodes, setAllQRCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalGenerated: 0,
    activeQRCodes: 0,
    totalScans: 0,
    expiringSoon: 0
  });

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (profileLoading) return;
    if (currentTenant?.id && user?.id) {
      fetchMyQRCodes();
      if (isStaffMember) {
        fetchAllQRCodes();
        fetchStats();
      }
    }
  }, [currentTenant?.id, user?.id, isStaffMember, profileLoading]);

  const fetchMyQRCodes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('user_id', user?.id)
        ?.eq('barcode_type', 'qr_code')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setMyQRCodes(data || []);
    } catch (error) {
      console.error('Error fetching QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllQRCodes = async () => {
    try {
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.select(`
          *,
          user_profiles!member_barcodes_user_id_fkey(full_name, email, avatar_url),
          tenant_members!inner(role, membership_plan, is_active)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('barcode_type', 'qr_code')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setAllQRCodes(data || []);
    } catch (error) {
      console.error('Error fetching all QR codes:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('barcode_type', 'qr_code');

      if (error) throw error;

      const now = new Date();
      const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const activeQRCodes = data?.filter(qr => {
        if (!qr?.is_active) return false;
        if (!qr?.expires_at) return true;
        return new Date(qr?.expires_at) > now;
      })?.length || 0;

      const expiringSoon = data?.filter(qr => {
        if (!qr?.is_active || !qr?.expires_at) return false;
        const expiryDate = new Date(qr?.expires_at);
        return expiryDate > now && expiryDate <= twentyFourHoursFromNow;
      })?.length || 0;

      const totalScans = data?.reduce((sum, qr) => sum + (qr?.scan_count || 0), 0);

      setStats({
        totalGenerated: data?.length || 0,
        activeQRCodes,
        totalScans,
        expiringSoon
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleQRGenerated = () => {
    fetchMyQRCodes();
    if (isStaffMember) {
      fetchAllQRCodes();
      fetchStats();
    }
  };

  const handleDeactivateQR = async (qrId) => {
    try {
      const { error } = await supabase
        ?.from('member_barcodes')
        ?.update({ is_active: false })
        ?.eq('id', qrId);

      if (error) throw error;

      fetchMyQRCodes();
      if (isStaffMember) {
        fetchAllQRCodes();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deactivating QR code:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            QR Code Management Center
          </h1>
          <p className="text-muted-foreground">
            Generate and manage QR codes for profile sharing and member account linking
          </p>
        </div>

        {/* Stats Cards - Staff Only */}
        {isStaffMember && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Generated</span>
                <Icon name="QrCode" size={20} className="text-primary" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                {stats?.totalGenerated}
              </p>
            </div>

            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Active QR Codes</span>
                <Icon name="CheckCircle" size={20} className="text-success" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                {stats?.activeQRCodes}
              </p>
            </div>

            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Scans</span>
                <Icon name="Scan" size={20} className="text-info" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                {stats?.totalScans}
              </p>
            </div>

            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Expiring Soon</span>
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                {stats?.expiringSoon}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('generate')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'generate' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Plus" size={18} className="inline mr-2" />
            Generate QR Code
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'active' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="List" size={18} className="inline mr-2" />
            Active QR Codes
          </button>
          {isStaffMember && (
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'history' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="History" size={18} className="inline mr-2" />
              History & Analytics
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'generate' && (
          <QRGenerationPanel
            onQRGenerated={handleQRGenerated}
            isStaffMember={isStaffMember}
          />
        )}

        {activeTab === 'active' && (
          <ActiveQRCodesList
            qrCodes={isStaffMember ? allQRCodes : myQRCodes}
            onDeactivate={handleDeactivateQR}
            loading={loading}
            isStaffMember={isStaffMember}
          />
        )}

        {activeTab === 'history' && isStaffMember && (
          <QRCodeHistoryPanel
            qrCodes={allQRCodes}
          />
        )}
      </div>
    </div>
  );
};

export default QRCodeManagementCenter;